import { breakpoint, debounce, getSiblings, throttleRaf } from '../../util/helpers';
import { setElementInert, getKeyboardFocusableElements } from '../../util/a11y/handle-inert';
import strings from '../../lang/strings.json';

/**
 * Done (mobile a11y):
 * hide inner card from SR
 * hide card bg image from SR
 * back button SR text // SR should read back-button as "back to..."
 * define buttons with submenu as expandable
 * focus on first item of list after selecting an expandable button
 * prevent Lv2 links from triggering firstLevelLinks click event
 * focus back on previously selected item // for both Lv1 & Lv2
 * set targetLevel to 0 when in Lv1 submenu
 *
 * Todo (mobile a11y):
 * selected link[expanded] should also be hidden when pressed (done)
 * Lv3 submenu reads Lv2 items (done)
 * main body is still accessible when nav is open (done)
 *
 */

const transitionDuration = 300;

export default {
  init() {
    if (!document.querySelectorAll('.ms-navigation').length) { return; }

    this.currentLang = document.querySelector('html').lang;
    this.startNavigation();
    this.startDesktopNav();
    this.scrollListener();
    this.resizeListener();
    this.startMobileAccessibility();
    this.startDesktopAccessibility();
    this.handleSkipToContent();
    this.addScrollbarClass();
    this.currentBreakpoint = breakpoint(),

    window.addEventListener('load', () => {
      this.handleCTACaret();
      this.handleHeroBasedClasses();
    });

    window.addEventListener('resize', debounce(() => {
      this.resizeListener();
      this.handleCTACaret();
    }), 200);
  },
  handleSkipToContent() {
    const content = document.querySelector('#content');
    const firstElem = document.querySelector('.content');

    if(firstElem) {
      firstElem.setAttribute('tabindex', '-1');
    }

    if(content) {
      content.setAttribute('tabindex', '-1');
      content.setAttribute('aria-label', strings[this.currentLang].articleContent);
    }

    // looks like iOS VO still respects href="#content" so I kept it and removed the manual handler
  },
  handleHeroBasedClasses() {
    const navContainer = document.querySelector('.ms-navigation');

    if (document.querySelector('.hero-no-media')) {
      // No hero nav class.
      navContainer.classList.add('ms-navigation--no-hero');
    } else if (document.querySelector('.ms-small-hero')) {
      // Nav class if hero is small-hero.
      navContainer.classList.add('ms-navigation--on-small-hero');
    }
  },
  startDesktopAccessibility() {
    // separating desktop here for readability.
    // global
    const wrap = document.querySelector('.wrap');
    const footer = document.querySelector('footer');

    const navContainer = document.querySelector('.ms-navigation');
    const backdrop = document.querySelector('.ms-navigation__backdrop');
    const desktopNav = navContainer.querySelector('.ms-navigation__desktop-nav');
    const desktopNavItems = navContainer.querySelector('.ms-navigation__desktop-nav > ul');
    const nextLevelItems = desktopNav.querySelectorAll('.first-level.has-next-level > a');
    const searchBtn = navContainer.querySelector('.ms-navigation__search');
    const langBtn = navContainer.querySelector('.ms-navigation__desktop-side .ms-navigation__language');
    const mainMenuItems = document.querySelectorAll('.ms-navigation__logo > a, .ms-navigation__desktop-nav > ul > li > a, .ms-navigation__desktop-side > a, .ms-navigation__search');
    const subMenu = document.querySelectorAll('.ms-navigation__desktop-secondary');
    let count = 0;
    let currentMenuIndex = 0;
    let currentSubmenuIndex = 0;

    // searchbox related
    searchBtn.addEventListener('click', (e) => {
      const fromActive = e.target.classList.contains('ms-navigation__search--active');
      const searchBox = e.target.previousElementSibling;
      const searchInput = searchBox.querySelector('input');

      if (fromActive) {
        setElementInert(false, searchBox);
        searchBtn.setAttribute('aria-label', strings[this.currentLang].closeSearchInput);

        setTimeout(() => {
          searchInput.focus();
        }, 100);

        // set these elements inert when opened
        setElementInert(true, [desktopNavItems, langBtn, wrap, footer])
      } else {
        setElementInert(true, searchBox);
        searchBtn.setAttribute('aria-label', strings[this.currentLang].openSearchInput);
        // set these elements inert when closed
        setElementInert(false, [desktopNavItems, langBtn, wrap, footer])
      }
    });

    // secondary nav-related
    const secondLevelLinks = desktopNav.querySelectorAll('.second-level.has-next-level > a');

    // close everything when backdrop is clicked
    backdrop.addEventListener('click', () => {
      nextLevelItems.forEach(a => a.setAttribute('aria-expanded', false));
      secondLevelLinks.forEach(a => a.setAttribute('aria-expanded', false));
    });

    // set inert of inner second level accordingly
    secondLevelLinks.forEach(sec => {
      sec.addEventListener('click', ({ target }) => {
        const parentLi = target.parentElement;
        const siblingTertiary = target.nextElementSibling;

        target.setAttribute('aria-expanded', true);
        setElementInert(false, siblingTertiary);

        // set every other tertiary's inert to true
        getSiblings(parentLi).forEach(li => {
          if (!li.classList.contains('.ms-ts-nav-cta')) {
            const relativeAnchor = li.querySelector('a');
            const relativeTertiary = li.querySelector('.ms-navigation__desktop-tertiary');

            relativeAnchor.setAttribute('aria-expanded', false);
            setElementInert(true, relativeTertiary);
          }
        });

        // Readd the recalculated sub menu index based on the opened tertiary menu
        setTimeout(() => {
          subMenu.forEach(sub => {
            const subMenuItems = sub.querySelectorAll('a');
            subMenuItems.forEach(a => {
              a.removeAttribute('data-sub-index');
            });
          });

          subMenu.forEach(sub => {
            let subLinksCount = 0;
            const subMenuItems = sub.querySelectorAll('a');
            subMenuItems.forEach(a => {
              if (a.tabIndex !== -1) {
                a.setAttribute('data-sub-index', subLinksCount);
                subLinksCount++;
              }
            });
          });

          currentSubmenuIndex = parseInt(target.getAttribute('data-sub-index'));
        })
      }, 400);
    });

    // set inert of inner secondary nav once their parent <li> is clicked
    nextLevelItems.forEach(li => {
      li.addEventListener('click', (e) => {
        const innerSecondary = e.target.nextElementSibling;
        const isExpanded = innerSecondary.classList.contains('ms-navigation__desktop-secondary--active');
        const isRecipeVariant = innerSecondary.classList.contains('ms-navigation__desktop-secondary--no-products');

        // set expanded accordingly
        e.target.setAttribute('aria-expanded', isExpanded);

        // set inert as how it is displayed
        setElementInert(!isExpanded, innerSecondary);

        // set global elements inert
        setElementInert(isExpanded, [wrap, footer]);

        // make sure all other siblings are hidden
        getSiblings(e.target.parentElement).forEach(sibling => {
          const siblingInnerSecondary = sibling.querySelector('.ms-navigation__desktop-secondary:not(.ms-navigation__desktop-secondary--no-products)');
          const siblingNoProduct = sibling.querySelector('.ms-navigation__desktop-secondary--no-products');
          const siblingAnchors = siblingInnerSecondary ? siblingInnerSecondary.querySelectorAll('ul > li:not(.ms-ts-nav-cta) > a') : null;
          const innerTertiaries = siblingInnerSecondary ? siblingInnerSecondary.querySelectorAll('ul > li:not(.ms-ts-nav-cta) .ms-navigation__desktop-tertiary') : null;

          if (siblingAnchors) {
            siblingAnchors.forEach(a => a.setAttribute('aria-expanded', false));
          }

          setElementInert(true, [siblingInnerSecondary, siblingNoProduct]);
          setElementInert(true, innerTertiaries);
        });

        /**
         * All codes here are for the Home/Kitchen layout, a separate code block for the Recipe & Tips layout is available below
         *
         * we'll also find the default opened active tertiary and remove its inert
         */
        if (!isRecipeVariant && isExpanded) {
          const activeAnchor = innerSecondary.querySelector('ul > li.active > a');
          const activeTertiary = innerSecondary.querySelector('ul > li.active .ms-navigation__desktop-tertiary');

          // set automatic inner anchor's expanded state to true
          activeAnchor.setAttribute('aria-expanded', true);
          // set inert to false, make sure that the others are true
          setElementInert(false, activeTertiary);
        }

        // Readd the recalculated sub menu index based on the opened tertiary menu
        setTimeout(() => {
          subMenu.forEach(sub => {
            let subLinksCount = 0;
            const subMenuItems = sub.querySelectorAll('a');
            subMenuItems.forEach(a => {
              if (a.tabIndex !== -1) {
                a.setAttribute('data-sub-index', subLinksCount);
                subLinksCount++;
              }
            });
          });
        }, 350)
      })
    });

    //Set Focus event on the main menu links
    mainMenuItems.forEach(item => {
      item.setAttribute('data-index', count);
      item.addEventListener('focus', e => {
        currentMenuIndex = e.target.getAttribute('data-index');
      })
      count++;
    });

    //Set Focus event on the sub menu links
    subMenu.forEach(sub => {
      const subMenuItems = sub.querySelectorAll('a');
      subMenuItems.forEach(a => {
        a.addEventListener('focus', e => {
          currentSubmenuIndex = e.target.getAttribute('data-sub-index');
        })
      });
    });

    window.addEventListener('keyup', e => {
      const firstSecLink = e.target.parentElement.querySelector('.ms-navigation__desktop-secondary ul li a');
      // If the Arrow down key was pressed on the First Level Menu with a sub menu
      if (e.code === 'ArrowDown' && e.target.parentElement.classList.contains('has-next-level') && e.target.parentElement.classList.contains('first-level')) {
        if (!e.target.classList.contains('active')) {
          e.target.click();
          setTimeout(() => {
            if (firstSecLink) {
              currentSubmenuIndex = 0;
              firstSecLink.focus();
            }
          }, 350)
        }
      }

      //Pressing up Again on the first level link that is active will close the secondary menu
      if (e.code === 'ArrowUp' && e.target.parentElement.classList.contains('has-next-level') && e.target.parentElement.classList.contains('first-level')) {
        if(e.target.classList.contains('active')) {
          e.target.click();
        }
      }

      //Pressing down again on the frist level link that is open will go back to the first item on the secondary menu
      if (e.code === 'ArrowDown' && e.target.parentElement.classList.contains('has-next-level') && e.target.parentElement.classList.contains('first-level')) {
        const firstSecLink = e.target.parentElement.querySelector('.ms-navigation__desktop-secondary ul li a');
        if (e.target.classList.contains('active')) {
          firstSecLink.focus();
        }
      }

      // Arrow Key Right to navigate on the mega menu to next
      if (e.code === 'ArrowRight' && e.target.closest('.ms-navigation__desktop-secondary')) {
        const targetItems = e.target.closest('.ms-navigation__desktop-secondary').querySelectorAll('a[data-sub-index]');
        if (targetItems.length - 1 > currentSubmenuIndex) {
          currentSubmenuIndex++;
          targetItems[currentSubmenuIndex].focus();
        }
      }

      // Arrow Key Down to navigate down on the left links on the secondary menu
      if (e.code === 'ArrowDown' && e.target.closest('.ms-navigation__desktop-secondary')) {
        // Check if the focus is on the left side links, navigate/focus to the next tab
        if (e.target.parentElement.nextElementSibling && !e.target.closest('.ms-navigation__desktop-secondary--no-products') && e.target.parentElement.classList.contains('left-links')) {
          const nextSubMenuLink = e.target.parentElement.nextElementSibling.querySelector('a');
          nextSubMenuLink.focus();
        } else { //otherwise just move the focus forwards accoring to the DOM order
          const targetItems = e.target.closest('.ms-navigation__desktop-secondary').querySelectorAll('a[data-sub-index]');
          if (targetItems.length - 1 > currentSubmenuIndex) {
            currentSubmenuIndex++;
            targetItems[currentSubmenuIndex].focus();
          }
        }
      }

      // Arrow Key Up to navigate up on the left links on the secondary menu
      if (e.code === 'ArrowUp' && e.target.closest('.ms-navigation__desktop-secondary')) {
        // Check if the focus is on the left side links, navigate/focus to the previous tab
        if (e.target.parentElement.previousElementSibling && !e.target.closest('.ms-navigation__desktop-secondary--no-products') && e.target.parentElement.classList.contains('left-links')) {
          const nextSubMenuLink = e.target.parentElement.previousElementSibling.querySelector('a');
          nextSubMenuLink.focus();
        } else { //otherwise just move the focus backwards accoring to the DOM order
          const targetItems = e.target.closest('.ms-navigation__desktop-secondary').querySelectorAll('a[data-sub-index]');
          if (currentSubmenuIndex > 0) {
            currentSubmenuIndex--;
            targetItems[currentSubmenuIndex].focus();
          } else if (parseInt(currentSubmenuIndex) === 0) {
            e.target.closest('.ms-navigation__desktop-secondary').parentElement.querySelector('.active').focus();
          }
        }
      }

      // Arrow Key Left to navigate on the mega menu links, backwards
      if (e.code === 'ArrowLeft' && e.target.closest('.ms-navigation__desktop-secondary')) {
        const targetItems = e.target.closest('.ms-navigation__desktop-secondary').querySelectorAll('a[data-sub-index]');
        if (currentSubmenuIndex > 0) {
          currentSubmenuIndex--;
          targetItems[currentSubmenuIndex].focus();
        } else if (parseInt(currentSubmenuIndex) === 0) {
          e.target.closest('.ms-navigation__desktop-secondary').parentElement.querySelector('.active').focus();
        }
      }

      // Arrow Key Right to navigate on the next menu on the first level menu
      if ((e.code === 'ArrowRight' && e.target.parentElement.classList.contains('first-level')) ||
          (e.code === 'ArrowRight' && e.target.parentElement.classList.contains('ms-navigation__logo')) ||
          (e.code === 'ArrowRight' && e.target.classList.contains('ms-navigation__language')) ||
          (e.code === 'ArrowRight' && e.target.classList.contains('ms-navigation__search'))) {
        if (mainMenuItems.length - 1 > currentMenuIndex) {
          currentMenuIndex++;
          mainMenuItems[currentMenuIndex].focus();
        }
      }

      // Arrow Key Left to navigate on the prev menu on the first level menu
      if ((e.code === 'ArrowLeft' && e.target.parentElement.classList.contains('first-level')) ||
        (e.code === 'ArrowLeft' && e.target.parentElement.classList.contains('ms-navigation__logo')) ||
        (e.code === 'ArrowLeft' && e.target.classList.contains('ms-navigation__language')) ||
        (e.code === 'ArrowLeft' && e.target.classList.contains('ms-navigation__search'))) {
        if (currentMenuIndex > 0) {
          currentMenuIndex--;
          mainMenuItems[currentMenuIndex].focus();
        }
      }
    });

    //Prevent window from scroll if using arrow keys on the navigation menu
    window.addEventListener('keydown', function (e) {
      if ([32, 37, 38, 39, 40].indexOf(e.keyCode) > -1 && e.target.closest('.first-level')) {
        e.preventDefault();
      }
    }, false);
  },
  startMobileAccessibility() {
    const navContainer = document.querySelector('.ms-navigation');
    const mainMenu = navContainer.querySelector('.ms-navigation__menu');
    const hamburgerBtn = navContainer.querySelector('.ms-navigation__hamburger');
    const primaryNav = navContainer.querySelector('.ms-navigation__primary-nav');
    const wrap = document.querySelector('.wrap');
    const footer = document.querySelector('footer');
    const siteLogo = navContainer.querySelector('.ms-navigation__logo');
    const langBtn = navContainer.querySelector('.ms-navigation__language');
    const miniCrumbs = navContainer.querySelector('.ms-navigation__mini-crumbs');
    const backdrop = document.querySelector('.ms-navigation__backdrop');

    // first-level elements
    const primaryLinks = primaryNav.querySelectorAll('.first-level');
    const firstLevelLinks = primaryNav.querySelectorAll('.first-level.has-next-level > a');

    // second-level elements
    let secondaryLinks = [];
    let secondLevelLinks = [];
    let Lv1activeEle = null;
    let Lv2activeEle = null;

    hamburgerBtn.addEventListener('click', () => {
      const isNavExpanded = navContainer.classList.contains('ms-navigation--expanded');

      if (isNavExpanded) {
        mobileOpenNav();
      } else { mobileCloseNav(); }
    });

    backdrop.addEventListener('click', () => {
      mobileCloseNav();
    });

    /**
     * First-level navigation mechanics
     *
     * 1. We allow SR to read the mini crumbs
     * 2. We hide inner and past menus
     * 3. We only show the current level
     */
    firstLevelLinks.forEach(el => {
      el.addEventListener('click', ({target}) => {
        setMiniCrumbsInert();

        // set the anchor tag inert
        setElementInert(true, target);

        // set every link inert
        getSiblings(el.parentElement).forEach(link => {
          setElementInert(true, link);
        });

        // save Lv1 expanded button
        Lv1activeEle = document.activeElement;
        // focus on first item of Lv2
        setTimeout(() => {
          getKeyboardFocusableElements(target.nextElementSibling).firstFocusable.focus();
        }, transitionDuration);

        // set the second level elements
        secondaryLinks = el.parentElement.querySelectorAll('.ms-navigation__secondary-nav--expanded li.second-level');
        secondLevelLinks = el.parentElement.querySelectorAll('.ms-navigation__secondary-nav--expanded li.second-level.has-next-level');

        /**
         * Second-level mechanics is basically similar to the first-level
         */
        secondLevelLinks.forEach(secondEl => {
          secondEl.addEventListener('click', (e) => {
            e.stopPropagation();

            // set element inert
            setElementInert(true, e.target);

            // save Lv2 expanded button
            Lv2activeEle = document.activeElement;
            // focus on first item of Lv3
            setTimeout(() => {
              getKeyboardFocusableElements(e.target.nextElementSibling).firstFocusable.focus();
            }, transitionDuration);

            miniCrumbs.dataset.thirdOpen = true;

            // set every link inert
            getSiblings(secondEl).forEach(secondLink => {
              setElementInert(true, secondLink);
            });
          });
        });

      })
    });

    // listen to mini-crumbs click event
    miniCrumbs.addEventListener('click', (e => {
      e.preventDefault();
      const target = parseInt(miniCrumbs.dataset.targetLevel);
      const thirdOpen = miniCrumbs.dataset.thirdOpen == 'true';

      // focus back on selected menu option
      if(!target) {
        // ...in Lev1
        setTimeout(() => {
          Lv1activeEle.focus();
        }, transitionDuration);
      } else {
        // ...in Lev2
        setTimeout(() => {
          Lv2activeEle.focus();
        }, transitionDuration);
      }

      if (!thirdOpen) {
        mobileCloseSecondLevel();
        mobileResetPrimary();
      } else {
        miniCrumbs.dataset.thirdOpen = false;
        mobileResetSecondary();
      }
    }));

    /**
     * set miniCrumbs inert
     */
    const setMiniCrumbsInert = () => {
      const isMiniCrumbsVisible = miniCrumbs.classList.contains('ms-navigation__mini-crumbs--visible');
      if (isMiniCrumbsVisible) {
        setElementInert(false, miniCrumbs);
      } else {
        setElementInert(true, miniCrumbs);
      }
    };

    const mobileCloseSecondLevel = () => {
      setElementInert(true, miniCrumbs);
    }

    /**
     * Open nav, setting inert values correctly
     */
    const mobileOpenNav = () => {
      // show the primary menu to the SR
      setElementInert(false, [mainMenu, primaryNav]);

      // hide logo, translation button
      setElementInert(true, [siteLogo, langBtn]);

      // hide other elements from SR
      setElementInert(true, [wrap, footer]);
    }

    /**
     * Reset all inert to their default states
     */
    const mobileCloseNav = () => {
      // hide the primary menu to the SR
      setElementInert(true, [mainMenu, primaryNav]);

      // show logo, translation button
      setElementInert(false, [siteLogo, langBtn]);

      // show other elements from SR
      setElementInert(false, [wrap, footer]);

      mobileCloseSecondLevel();
      mobileResetPrimary();
    }

    /**
     * Reset Primary nav a11y; make focusable
     */
    const mobileResetPrimary = () => {
      // set every first level item's aria and inert back
      primaryLinks.forEach(el => {
        // except separator
        if (!el.classList.contains('separator')) {
          setElementInert(false, [el, el.querySelector('a')]);
        }
      });
    }

    /**
     * Reset Secondary Nav a11y; make focusable
     */
    const mobileResetSecondary = () => {
      // set every first level item's aria and inert back
      secondaryLinks.forEach(el => {
        // except separator
        if (!el.classList.contains('separator')) {
          setElementInert(false, [el, el.querySelector('a')]);
        }
      });
    }
  },
  resizeListener() {
    if (breakpoint() !== this.currentBreakpoint) {
      this.cleanUp();
      this.currentBreakpoint = breakpoint();
    }
    document.documentElement.style.setProperty('--scrollbar-width', (window.innerWidth - document.documentElement.clientWidth) + 'px');
  },
  cleanUp() {
    const navContainer = document.querySelector('.ms-navigation');
    const miniCrumbs = navContainer.querySelector('.ms-navigation__mini-crumbs');
    const backdrop = document.querySelector('.ms-navigation__backdrop');
    const navHead = navContainer.querySelector('.ms-navigation__head');
    const desktopNav = navContainer.querySelector('.ms-navigation__desktop-nav');
    const secondaryNavs = document.querySelectorAll('.ms-navigation__desktop-secondary');
    const desktopActive = desktopNav ? desktopNav.querySelector('a.active') : null;
    const html = document.querySelector('html');

    // clear mini-crumbs
    if (miniCrumbs) miniCrumbs.classList.remove('ms-navigation__mini-crumbs--visible'); miniCrumbs.dataset.targetLevel = 0;

    // clear main navigation parent
    navContainer.classList.remove('ms-navigation--expanded');

    // clear backdrop
    if (backdrop) backdrop.classList.remove('ms-navigation__backdrop--visible');

    // clear navhead
    if (navHead) navHead.classList.remove('ms-navigation__head--expanded');

    // clear secondary navs
    if (secondaryNavs) secondaryNavs.forEach(nav => {
      nav.classList.remove('ms-navigation__desktop-secondary--active');
      nav.removeAttribute('style');
    });

    // clear desktop active link
    if (desktopActive) desktopActive.classList.remove('active');

    // clear scroll lock
    html.classList.remove('no-scroll');
  },
  // strictly desktop nav
  startDesktopNav() {
    const navContainer = document.querySelector('.ms-navigation');
    const backdrop = document.querySelector('.ms-navigation__backdrop');
    const navHead = navContainer.querySelector('.ms-navigation__head');
    const desktopNav = navContainer.querySelector('.ms-navigation__desktop-nav');
    const nextLevelItems = desktopNav.querySelectorAll('.first-level.has-next-level > a');
    const secondaryLinks = desktopNav.querySelectorAll('.second-level:not(.ms-ts-nav-cta) > a');
    const collections = desktopNav.querySelector('.ms-navigation__collections');
    const html = document.querySelector('html');
    const searchBtn = navContainer.querySelector('.ms-navigation__search');
    const searchSubmit = navContainer.querySelector('.ms-navigation__search-submit');
    const searchBox = navContainer.querySelector('.ms-navigation__search-box');
    const searchInput = searchBox.querySelector('input');
    // const noProductUls = desktopNav.querySelectorAll('.ms-navigation__desktop-secondary--no-products ul ul');

    // const setupCollections = () => {
    //   let tallest = 0;

    //   if (noProductUls.length) {
    //     noProductUls.forEach(ul => {
    //       tallest = ul.getBoundingClientRect().height > tallest ? ul.getBoundingClientRect().height : tallest;
    //     })
    //   }

    //   return tallest;
    // }

    // search bar close mechanism
    searchBtn.addEventListener('click', (e) => {
      const willSubmit = e.target.classList.contains('ms-navigation__search--active');

      if (!willSubmit) {
        searchBox.classList.add('ms-navigation__search-box--active');
        searchBtn.classList.add('ms-navigation__search--active');
      } else {
        closeSearchBox();
      }
    });

    // submit button listener
    searchSubmit.addEventListener('click', () => {
      if (searchInput.value && searchInput.value !== '') {
        submitSearchQuery(searchInput.value);
      }
    });

    // listen on enter key press
    searchInput.addEventListener('keyup', (e) => {

      if ((e.target.value !== '')) {
        searchSubmit.classList.add('ms-navigation__search-submit--active');
      } else {
        searchSubmit.classList.remove('ms-navigation__search-submit--active');
      }

      const key = e.key || e.keyCode; // keyCode is deprecated but we'll use it as a fallback
      if ((key === 13 || key === 'Enter') && (e.target.value && e.target.value !== '')) {
        submitSearchQuery(e.target.value);
      }
    });

    // consolidated submit function
    const submitSearchQuery = (query) => {
      // important to encode the input to prevent breaking with unescaped characters
      const url = `//${location.host}/search/${encodeURIComponent(query)}`;
      location.href = url;
    }

    const closeSearchBox = () => {
      searchBox.classList.remove('ms-navigation__search-box--active');
      searchBtn.classList.remove('ms-navigation__search--active');
      searchSubmit.classList.remove('ms-navigation__search-submit--active');
      searchBox.querySelector('input').value = null; // reset search box's value
    }

    const setActiveDrawerHeight = (secondary, tertiary) => {
      secondary.removeAttribute('style'); // clean up first

      // if tertiary's height is smaller than the secondary, we keep secondary's height.
      if (tertiary.getBoundingClientRect().height < secondary.getBoundingClientRect().height) { return; }

      secondary.style.height = `${tertiary.getBoundingClientRect().height}px`;
    }

    // tertiary start
    const setupTertiaryMenu = () => {
      const activeSecondLevel = desktopNav.querySelectorAll('.second-level');

      // set tertiary offset via active parent's offset top
      activeSecondLevel.forEach(active => {
        const tertiary = active.querySelector('.ms-navigation__desktop-tertiary');

        if (tertiary) {
          tertiary.style.top = `-${active.offsetTop}px`;
        }
      });
    }

    const cleanUpTertiary = () => {
      const allTertiary = desktopNav.querySelectorAll('.ms-navigation__desktop-tertiary');

      allTertiary.forEach(menu => {
        const parentSecondary = menu.parentElement.parentElement.parentElement;

        parentSecondary.removeAttribute('style');
        menu.removeAttribute('style');
      });
    }

    // lsten to all secondary links click
    secondaryLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        const parentLi = e.target.parentElement;

        if (!parentLi.classList.contains('has-next-level')) {
          return;
        }

        e.preventDefault();
        const nextTertiary = e.target.nextElementSibling;
        const parentSecondary = e.target.parentElement.parentElement.parentElement;

        if (nextTertiary) {
          nextTertiary.style.top = `-${parentLi.offsetTop}px`;
          setActiveDrawerHeight(parentSecondary, nextTertiary);
        }

        // append active class
        parentLi.classList.add('active');

        // remove other active states
        getSiblings(parentLi).forEach(li => {
          li.classList.remove('active');
        })
      })
    })

    // black overlay click listener
    backdrop.addEventListener('click', () => {
      // close backdrop
      backdrop.classList.remove('ms-navigation__backdrop--visible');
      navHead.classList.remove('ms-navigation__head--expanded');
      // remove navContainer expanded class
      navContainer.classList.remove('ms-navigation--desktop-open');
      // remove nav active state
      navHead.classList.remove('ms-navigation__head--expanded');
      // remove secondary nav
      navContainer.querySelectorAll('.ms-navigation__desktop-secondary').forEach(i => i.classList.remove('ms-navigation__desktop-secondary--active'));
      navContainer.querySelectorAll('a.active').forEach(a => a.classList.remove('active'));

      if (collections) collections.removeAttribute('style');

      // cleanup
      cleanUpTertiary();
    })

    // Prevent default for tems with next level, show their dropdowns instead
    nextLevelItems.forEach(li => {
      li.addEventListener('click', (e) => {
        e.preventDefault();
        const target = e.target;
        const activeSecondary = target.nextElementSibling;

        if (!target.classList.contains('active')) {
          const parentLi = target.parentElement;

          // remove other active <li></li>
          getSiblings(parentLi).forEach(el => {
            const innerAnchor = el.querySelector('a');
            const innerSecondary = el.querySelector('.ms-navigation__desktop-secondary');

            // only remove if an anchor link exists
            if (innerAnchor) {
              innerAnchor.classList.remove('active');
            }

            // remove if a secondary menu is present inside
            if (innerSecondary) {
              innerSecondary.classList.remove('ms-navigation__desktop-secondary--active');
            }
          })

          // assign active class
          target.classList.add('active');

          html.classList.add('no-scroll');

          // assign navContainer expanded class
          navContainer.classList.add('ms-navigation--desktop-open');
          // assign expanded class to the parent navigation
          navHead.classList.add('ms-navigation__head--expanded');
          // open backdrop
          backdrop.classList.add('ms-navigation__backdrop--visible');
          // open secondary nav
          activeSecondary.classList.add('ms-navigation__desktop-secondary--active');

          // trigger tertiary menu's offset setting
          setupTertiaryMenu();

          // manipulate height of active secondary based on active tertiary
          const tertiary = activeSecondary.querySelector('li.active > .ms-navigation__desktop-tertiary');

          if (tertiary) {
            setActiveDrawerHeight(activeSecondary, tertiary);
          }

          // setup collections' margin
          // if (collections) collections.style.marginTop = `${((setupCollections() / 2) + 20) > 180 ? ((setupCollections() / 2) + 20) : 180}px`; // 20 = base margin
        } else {
          // if element is already active, close it instead
          target.classList.remove('active');

          // remove scroll lock
          html.classList.remove('no-scroll');

          // close backdrop
          backdrop.classList.remove('ms-navigation__backdrop--visible');

          // remove navContainer expanded class
          navContainer.classList.remove('ms-navigation--desktop-open');
          // remove nav active state
          navHead.classList.remove('ms-navigation__head--expanded');
          // remove secondary nav
          activeSecondary.classList.remove('ms-navigation__desktop-secondary--active');
          // cleanup opened recipe & tips nav tab
          navContainer.querySelectorAll('.ms-navigation__desktop-secondary--no-products').forEach(tab => tab.removeAttribute('style'));

          if (collections) collections.removeAttribute('style');

          // cleanup
          cleanUpTertiary();
        }
      });
    })
  },
  // strictly mobile nav
  startNavigation() {
    const navContainer = document.querySelector('.ms-navigation');
    const mainMenu = document.querySelector('.ms-navigation__menu');
    const primaryNav = document.querySelector('.ms-navigation__primary-nav');
    const hamburgerBtn = document.querySelector('.ms-navigation__hamburger');
    const backdrop = document.querySelector('.ms-navigation__backdrop');
    const html = document.querySelector('html');
    const hasNextLevel = mainMenu.querySelectorAll('.has-next-level');
    const miniCrumbs = document.querySelector('.ms-navigation__mini-crumbs');
    const miniCrumbsText = document.querySelector('.ms-navigation__mini-crumbs > .ms-navigation__prev span');
    const navHead = document.querySelector('.ms-navigation__head');

    const closeEverything = () => {
      // check if nav is open, close it if it's true
      if (primaryNav.classList.contains('ms-navigation__primary-nav--second-level-open')) {
        miniCrumbs.classList.remove('ms-navigation__mini-crumbs--visible')
        primaryNav.classList.remove('ms-navigation__primary-nav--second-level-open');
        document.querySelectorAll('.ms-navigation__secondary-nav--expanded').forEach(el => {
          el.classList.remove('ms-navigation__secondary-nav--expanded')
          el.removeAttribute('style');
        });
      }

      // always set mainMenu to scrollTop = 0
      mainMenu.scrollTop = 0;

      // close all opened menus
      mainMenu.querySelectorAll('.ms-navigation__secondary-nav--expanded .opened').forEach(el => {
        el.classList.remove('opened');
      });

      // hide the mini crumbs again
      miniCrumbs.classList.remove('ms-navigation__mini-crumbs--visible');

      // when target is 1, always return the height base on the primary nav (first level)
      primaryNav.removeAttribute('style');
      mainMenu.style.height = `${primaryNav.getBoundingClientRect().height}px`;

      setTimeout(() => {
        mainMenu.removeAttribute('style');
      }, transitionDuration);

      // bring all first-level items their visiblity back
      primaryNav.querySelectorAll('li.first-level').forEach(el => {
        el.classList.remove('hidden');
        el.classList.remove('opened')
      })

      // bring all second-level items their visiblity back
      primaryNav.querySelectorAll('li.second-level').forEach(el => {
        el.classList.remove('hidden');
        el.classList.remove('opened')
      })
    }

    const closeThirdLevel = () => {
      // always set mainMenu to scrollTop = 0
      mainMenu.scrollTop = 0;

      // close third level opened menus
      mainMenu.querySelectorAll('.ms-navigation__secondary-nav--expanded .ms-navigation__secondary-nav--expanded').forEach(el => {
        el.classList.remove('ms-navigation__secondary-nav--expanded');
      });

      // close second-level `opened` class
      mainMenu.querySelectorAll('.ms-navigation__secondary-nav--expanded .opened').forEach(el => {
        const parentSecondaryMenu = el.parentElement.parentElement;
        // when going back to the 2nd-level, set the height of the mainMenu to the height of the 2nd-level instead of the primary
        mainMenu.style.height = `${parentSecondaryMenu.getBoundingClientRect().height + 75}px`; // 75px padding top and bottom

        // reset mini crumbs
        miniCrumbs.dataset.targetLevel = 1;
        miniCrumbsText.textContent = parentSecondaryMenu.previousElementSibling.textContent;

        el.classList.remove('opened');
      });

      // wait for the animation to finish
      setTimeout(() => {
        // 65 = navigation header height
        if (mainMenu.getBoundingClientRect().height + 65 > window.innerHeight) {
          mainMenu.style.overflow = 'scroll';
        }
      }, transitionDuration)

      // bring all second-level items their visiblity back
      primaryNav.querySelectorAll('li.second-level').forEach(el => {
        el.classList.remove('hidden');
        el.classList.remove('opened')
      })
    }

    // hamburger button click listener
    hamburgerBtn.addEventListener('click', () => {
      navContainer.classList.toggle('ms-navigation--expanded');

      const isNavExpanded = navContainer.classList.contains('ms-navigation--expanded');
      const isSafariMobile = navigator.userAgent.match(/iphone|ipod|ipad/i);
      const navHeight = navHead.getBoundingClientRect().height;
      const { y } = window.MS.scrollObject;
      miniCrumbs.dataset.targetLevel = 0;

      // Set menu's max height
      if (isNavExpanded) {
        document.body.scrollTop = y;
        navContainer.classList.remove('ms-navigation--docked');

        // change aria-label
        hamburgerBtn.setAttribute('aria-label', strings[this.currentLang].closeMenu);

        if (isSafariMobile) { html.classList.add('safari-nav-open'); }

        // Not sure why we set max height here but it's messing up with the height of the secondary nav so I'm commenting it out for now

        // setTimeout(() => {
        //   const navPanelMaxHeight = window.innerHeight - navHead.getBoundingClientRect().height;
        //   mainMenu.style.maxHeight = `${navPanelMaxHeight}px`;
        // }, transitionDuration);
      } else {
        // bring back the docked version if the current scroll position is larger than the nav's height
        if (y > navHeight) {
          navContainer.classList.add('ms-navigation--docked');
        }

        // change aria-label
        hamburgerBtn.setAttribute('aria-label', strings[this.currentLang].openMenu);

        html.classList.remove('safari-nav-open');
        closeEverything();
      }

      html.classList.toggle('no-scroll');
    })

    // black overlay click listener
    backdrop.addEventListener('click', () => {
      const navHeight = navHead.getBoundingClientRect().height;
      const { y } = window.MS.scrollObject;

      // bring back the docked version if the current scroll position is larger than the nav's height
      if (y > navHeight) navContainer.classList.add('ms-navigation--docked');

      closeEverything();
      html.classList.remove('no-scroll');
      navContainer.classList.remove('ms-navigation--expanded');
    })

    // if an item that has a next level is clicked, suppress the event and present the next level of menu
    hasNextLevel.forEach((link) => {
      const item = link.querySelector('a');

      item.addEventListener('click', (e) => {
        e.preventDefault();
        // always set mainMenu to scrollTop = 0
        mainMenu.scrollTop = 0;

        const secondaryNav = e.target.nextElementSibling;
        const parentLi = e.target.parentElement;
        let extraCardHeight = 0;
        let actualHeight = 0;


        // hide other parent <li></li>
        getSiblings(parentLi).forEach(li => {
          li.classList.add('hidden');
        });

        // enable breadcrumbs
        miniCrumbs.classList.add('ms-navigation__mini-crumbs--visible');
        miniCrumbs.dataset.targetLevel = parentLi.classList.contains('first-level') ? 1 : 2;
        miniCrumbsText.textContent = e.target.textContent;

        // show mechanism of the next level of menu
        parentLi.classList.add('opened');
        primaryNav.classList.add('ms-navigation__primary-nav--second-level-open');
        secondaryNav.classList.add('ms-navigation__secondary-nav--expanded');

        // check if the window is not yet large tablet
        // we execute this code only when it's not large tablet yet
        if (window.innerWidth >= 768 && window.innerWidth < 1024) {
          // check for the presence of a card.
          // The card is positioned absolute that's why there will be no recorded height of it
          // that is being added to the parent's height
          const insideCard = secondaryNav.querySelector('.ms-navigation__card');

          if (insideCard) {
            extraCardHeight = insideCard.getBoundingClientRect().height;
          }
        }

        const isSafariMobile = navigator.userAgent.match(/iphone|ipod|ipad/i);

        // height settlement
        // if the card inside is taller, use it as the base height.
        // else just use the height of the nav itself
        actualHeight = secondaryNav.getBoundingClientRect().height > extraCardHeight ? secondaryNav.getBoundingClientRect().height : extraCardHeight;

        // adjust the bottom part of the nav menu depending on the current breakpoint
        const extraHeight = this.currentBreakpoint === 'mobile' ? 30 : 77;

        // compute height of the secondary nav
        // we assign the overflow first before manipulating the height because this will mess up the view
        mainMenu.style.overflow = (actualHeight + extraHeight) > window.innerHeight ? 'scroll' : 'hidden';

        // evaluate if we assign 100vh or 100vh - 180px
        const baseHeight = isSafariMobile ? `calc(${((window.innerHeight - extraHeight) * 0.01)}px * 100)` : '100vh';

        // on mobile, since the CSS has extra 30px card margin at the bottom, we deduct it here.
        // if on tablet, we add the extra height instead as padding.
        mainMenu.style.height = (actualHeight + extraHeight) < window.innerHeight ? `${this.currentBreakpoint === 'mobile' ? actualHeight - extraHeight : actualHeight + extraHeight}px` : baseHeight;
      });
    });

    // mini crumbs click listener
    miniCrumbs.addEventListener('click', (e) => {
      e.preventDefault();
      const target = parseInt(e.target.dataset.targetLevel);

      // First level target
      // if (target === 1) {
      //   miniCrumbs.dataset.targetLevel = 0;
      //   closeEverything();
      // } else if (target === 2) {
      //   closeThirdLevel();
      // }

      // switchy
      switch(target) {
        case 2:
          closeThirdLevel();
          break;
        case 1:
          closeEverything();
          miniCrumbs.dataset.targetLevel = 0;
          hamburgerBtn.focus();
          break;
        default: // case 0
          closeEverything();
          hamburgerBtn.focus();
      }
    });
  },
  scrollListener() {
    const navContainer = document.querySelector('.ms-navigation');
    const navHead = document.querySelector('.ms-navigation__head');

    let isNavDocked = false;
    window.addEventListener('scroll', throttleRaf(() => {
      const navHeight = navHead.getBoundingClientRect().height;
      const { y } = window.MS.scrollObject;

      if (y > navHeight && !isNavDocked) {
        isNavDocked = true;
        navContainer.classList.add('ms-navigation--docked')
      } else if (y <= navHeight && isNavDocked) {
        isNavDocked = false;
        navContainer.classList.remove('ms-navigation--docked')
      }
    }));
  },
  handleCTACaret() {
    const links = document.querySelectorAll('.ms-navigation__product--cta a, .ms-navigation__desktop-top .ms-ts-nav-li-secondary--tips a');

    links.forEach(link => {
      const span = link.querySelector('span');
      const svg = link.querySelector('svg');

      // Decrease the span width of SVG to force creating a new line
      if (span.clientWidth >= link.clientWidth) {
        span.style.width = `${span.clientWidth - (svg.clientWidth + 5)}px`; // 5px offset
      }
    });
  },
  addScrollbarClass() {
    if (window.innerWidth - document.documentElement.clientWidth === 0) {
      const navContainer = document.querySelector('.ms-navigation');
      navContainer.classList.add('ms-navigation--no-scrollbar');
    }
  },
}



// WEBPACK FOOTER //
// /private/var/www/Morton-Windsor/wp-content/themes/morton-salt/resources/assets/scripts/multisite/modules/navigation.js